import { useEffect, useState } from "react";
import { FileArrowDown } from "@phosphor-icons/react";
import api, { apiError } from "../../lib/api";
import { downloadStatement } from "../../lib/download";
import { inr, pct, gainClass } from "../../lib/format";
import AppLayout, { PageLoader, thCls, tdCls, rowCls } from "../../components/AppLayout";
import { AllocationDonut, PALETTE } from "../../components/charts";
import StatCard from "../../components/ui/StatCard";

export default function PortfolioPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/investor/holdings").then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
  }, []);

  if (error) return <AppLayout title="Portfolio"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!data) return <AppLayout title="Portfolio"><PageLoader /></AppLayout>;

  const { metrics, allocation, holdings } = data;

  return (
    <AppLayout title="Portfolio" subtitle="Your complete investment composition across asset classes.">
      <div className="fade-up space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          <StatCard label="Invested Value" value={inr(metrics.total_invested)} testId="pf-invested" />
          <StatCard label="Current Value" value={inr(metrics.current_value)} testId="pf-current" />
          <StatCard label="Absolute Return" value={inr(metrics.gain_loss)} delta={metrics.gain_loss_pct} testId="pf-abs-return" />
          <StatCard label="CAGR" value={metrics.cagr !== null ? pct(metrics.cagr) : "—"} testId="pf-cagr" />
          <StatCard label="XIRR" value={metrics.xirr !== null ? pct(metrics.xirr) : "—"} testId="pf-xirr" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="glass-card p-6">
            <h3 className="font-heading font-semibold text-white mb-4">Asset Allocation</h3>
            <AllocationDonut data={allocation} testId="pf-allocation-chart" />
          </div>
          <div className="glass-card p-6">
            <h3 className="font-heading font-semibold text-white mb-5">Allocation Breakdown</h3>
            <div className="space-y-4" data-testid="allocation-breakdown">
              {allocation.map((a, i) => (
                <div key={a.category}>
                  <div className="flex items-center gap-3 mb-1.5">
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
                    <span className="text-sm text-zinc-300 flex-1">{a.category}</span>
                    <span className="tnum text-xs text-zinc-500">{pct(a.pct, 1)}</span>
                    <span className="tnum text-sm font-medium text-white w-28 text-right">{inr(a.value)}</span>
                  </div>
                  <div className="h-1 rounded-full bg-white/[0.05] ml-5">
                    <div className="h-1 rounded-full" style={{ width: `${a.pct * 100}%`, background: PALETTE[i % PALETTE.length] }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="glass-card overflow-hidden">
          <div className="px-6 pt-5 pb-4 flex items-center justify-between">
            <h3 className="font-heading font-semibold text-white">Holdings by Asset Class</h3>
            <button data-testid="download-portfolio-statement"
              onClick={() => downloadStatement("/statements/my/portfolio.pdf", "KMC-Portfolio-Statement.pdf")}
              className="flex items-center gap-2 text-xs font-bold rounded-lg px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-[#030706] emerald-glow transition-colors duration-200">
              <FileArrowDown size={15} weight="bold" /> Statement (PDF)
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pf-holdings-table">
              <thead>
                <tr className="border-y border-white/[0.06]">
                  <th className={thCls}>Asset / Fund</th>
                  <th className={thCls}>Class</th>
                  <th className={`${thCls} text-right`}>Invested</th>
                  <th className={`${thCls} text-right`}>Current</th>
                  <th className={`${thCls} text-right`}>Gain/Loss</th>
                  <th className={`${thCls} text-right`}>Return</th>
                  <th className={`${thCls} text-right`}>Allocation</th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((h) => (
                  <tr key={h.id} className={rowCls}>
                    <td className={`${tdCls} font-medium text-white`}>{h.asset_name}</td>
                    <td className={`${tdCls} text-zinc-500 text-xs`}>{h.category}</td>
                    <td className={`${tdCls} text-right tnum text-zinc-300`}>{inr(h.invested_amount)}</td>
                    <td className={`${tdCls} text-right tnum text-zinc-200`}>{inr(h.current_value)}</td>
                    <td className={`${tdCls} text-right tnum ${gainClass(h.gain_loss)}`}>{inr(h.gain_loss)}</td>
                    <td className={`${tdCls} text-right tnum font-semibold ${gainClass(h.return_pct)}`}>{pct(h.return_pct)}</td>
                    <td className={`${tdCls} text-right tnum text-zinc-400`}>{pct(h.allocation_pct, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
